<template>
  <v-card flat dark class="pa-2">
    <v-app-bar color="transparent" width="100%" elevation="0" class="justify-space-between py-2">
      <v-col cols="1" class="pl-0">
        <v-btn @click="$emit('close-view')" style="z-index: 10000" icon>
          <v-icon color="white">mdi-chevron-left</v-icon>
        </v-btn>
      </v-col>
      <v-col cols="10">
        <v-card-title class="ml-2 font-weight-bold">Krankenkasse</v-card-title>
      </v-col>
      <v-col></v-col>
    </v-app-bar>

    <v-card-text>
      <transition-group name="list" tag="div">
        <div v-for="(contractType, index) of contractTimespans" :key="'contractType' + index">
          <v-row no-gutters>
            <v-card-title class="font-weight-bold white--text">{{ contractType[0].title }}</v-card-title>
          </v-row>
          <v-row no-gutters>
            <v-col cols="12" v-for="(contract, cIndex) of contractType" :key="'contract' + cIndex">
              <v-card
                class="mb-4 mx-1 contract-card"
                :class="{
                  'active-contract-glow': contract.active && !contract.future,
                  'grey darken-3': contract.hasOpenPayment /* Dunklerer Hintergrund bei Schulden */,
                }"
                outlined>
                <v-list-item three-line>
                  <v-list-item-content>
                    <v-list-item-title class="text-h6 font-weight-medium mb-2 d-flex align-center">
                      Laufzeit {{ contractType.length - cIndex }} ({{ $moment(contract.start, "DD.MM.YYYY").format("YYYY") }})

                      <v-chip v-if="contract.hasOpenPayment" color="error" small class="ml-3 font-weight-bold" label>
                        <v-icon left x-small>mdi-alert-circle-outline</v-icon>
                        Offene Rechnung
                      </v-chip>
                    </v-list-item-title>
                    <v-list-item-subtitle class="font-weight-regular"> Gültig vom {{ contract.start }} bis {{ contract.end }} </v-list-item-subtitle>
                  </v-list-item-content>

                  <v-list-item-action>
                    <v-tooltip bottom>
                      <template #activator="{ on, attrs }">
                        <v-btn
                          fab
                          depressed
                          large
                          :disabled="contract.hasOpenPayment"
                          :color="getColorForRequestState(contract.request)"
                          @click="requestHealthcareFile(contract)"
                          v-bind="attrs"
                          v-on="on">
                          <v-icon color="white">{{ getIconForRequestState(contract.request) }}</v-icon>
                        </v-btn>
                      </template>
                      <span>{{
                        contract.hasOpenPayment ? "Bitte begleiche zuerst deine offenen Rechnungen" : getTooltipText(contract.request)
                      }}</span>
                    </v-tooltip>
                  </v-list-item-action>
                </v-list-item>
              </v-card>
            </v-col>
          </v-row>
        </div>
      </transition-group>
    </v-card-text>

    <v-dialog v-model="unlockDialog" dark persistent max-width="450px" transition="dialog-bottom-transition">
      <v-card v-if="selectedContract" class="pa-4 text-center rounded-xl">
        <v-card-title class="justify-center text-h5 font-weight-bold">{{ unlockDialogTitle }}</v-card-title>
        <v-card-text class="mt-2">
          <v-icon :color="getColorForRequestState(selectedContract.request)" size="80" class="my-4">{{
            getIconForRequestState(selectedContract.request)
          }}</v-icon>
          <p class="body-1">{{ unlockDialogText }}</p>
        </v-card-text>
        <v-card-actions class="justify-space-around">
          <v-btn color="grey" text @click="unlockDialog = false" large>Abbrechen</v-btn>
          <v-btn
            v-if="unlockDialogBtnText"
            :color="getColorForRequestState(selectedContract.request)"
            depressed
            darken-1
            rounded
            large
            @click="handleUnlockAction()">
            {{ unlockDialogBtnText }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="pdfDialog" dark persistent fullscreen transition="dialog-bottom-transition">
      <v-card v-if="selectedContract">
        <v-window v-model="pdfStep">
          <v-window-item :value="1">
            <v-card-title class="text-h5 px-5 mb-2 primary--text font-weight-bold">
              <v-icon left color="primary">mdi-form-textbox</v-icon>
              Zusätzliche Informationen
            </v-card-title>
            <v-card-subtitle>Bitte fülle diese Felder für die Bestätigung aus.</v-card-subtitle>
            <v-form v-model="isFormValid" ref="pdfForm">
              <v-card-text>
                <v-row dense>
                  <v-col cols="12">
                    <v-text-field
                      label="Name der Krankenversicherung"
                      outlined
                      v-model="customerInfos.healthcareName"
                      :rules="[rules.required]"></v-text-field>
                  </v-col>
                  <v-col cols="12">
                    <v-text-field label="Zusatzversicherung (optional)" outlined v-model="customerInfos.additionalInsurance"></v-text-field>
                  </v-col>
                  <v-col cols="12">
                    <v-text-field
                      label="Versichertennummer"
                      outlined
                      v-model="customerInfos.insuranceNumber"
                      :rules="[rules.required]"></v-text-field>
                  </v-col>
                  <v-col cols="6">
                    <v-text-field label="Vorname" readonly disabled outlined v-model="customerInfos.firstName"></v-text-field>
                  </v-col>
                  <v-col cols="6">
                    <v-text-field label="Nachname" readonly disabled outlined v-model="customerInfos.lastName"></v-text-field>
                  </v-col>
                  <v-col cols="12">
                    <v-text-field label="Strasse" readonly disabled outlined v-model="customerInfos.street"></v-text-field>
                  </v-col>
                  <v-col cols="4">
                    <v-text-field type="number" readonly disabled label="PLZ" outlined v-model="customerInfos.zip"></v-text-field>
                  </v-col>
                  <v-col cols="8">
                    <v-text-field label="Stadt" readonly disabled outlined v-model="customerInfos.city"></v-text-field>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-form>
          </v-window-item>

          <v-window-item :value="2">
            <v-card-title class="text-h5 px-5 mb-2 primary--text font-weight-bold">
              <v-icon left color="primary">mdi-draw</v-icon>
              Unterschrift
            </v-card-title>
            <v-card-subtitle>Bitte unterschreibe im untenstehenden Feld.</v-card-subtitle>
            <v-card-text>
              <signature-pad
                whiteBg
                class="mx-auto signature-pad"
                :signature-data="customerInfos.signature"
                @resetDrawing="resetSignature"
                @saveSignature="saveSignature"
                ref="signature"></signature-pad>
            </v-card-text>
          </v-window-item>
        </v-window>

        <v-card-actions class="pa-4">
          <v-btn color="grey" text @click="pdfStep === 1 ? (pdfDialog = false) : (pdfStep = 1)">
            {{ pdfStep === 1 ? "Abbrechen" : "Zurück" }}
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn v-if="pdfStep === 1" color="primary" depressed rounded @click="validateAndProceed()">Weiter</v-btn>
          <v-btn v-if="pdfStep === 2" color="primary" depressed rounded @click="downloadPDF()">
            <v-icon left>mdi-download</v-icon>
            Herunterladen
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="4000" bottom right>
      <v-icon left color="white">{{ snackbar.icon }}</v-icon>
      <span class="font-weight-bold white--text">{{ snackbar.text }}</span>
      <template #action="{ attrs }">
        <v-btn icon v-bind="attrs" @click="snackbar.show = false"><v-icon color="white">mdi-close</v-icon></v-btn>
      </template>
    </v-snackbar>
  </v-card>
</template>

<style scoped>
/* DARK MODE: Farbverlauf auf Grün angepasst */
.app-bar-gradient {
  background-image: linear-gradient(135deg, #00963f 0%, #1a2a22 100%);
  width: 100%;
  height: 100%;
}

.contract-card {
  border-radius: 16px !important;
  transition: all 0.3s ease-in-out;
  /* DARK MODE: Hintergrund der Karte für besseren Kontrast leicht anpassen */
  background-color: rgba(255, 255, 255, 0.05) !important;
}

.contract-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.2);
}

/* DARK MODE: "Glow"-Effekt auf Grün geändert */
.active-contract-glow {
  box-shadow: 0 0 15px rgba(0, 150, 63, 0.6);
  border-color: #00963f;
}

.signature-pad {
  border: 2px dashed #616161; /* Dunklerer Rand für Dark Mode */
  border-radius: 12px;
}

.list-enter-active,
.list-leave-active {
  transition: all 0.5s cubic-bezier(0.55, 0, 0.1, 1);
}
.list-enter,
.list-leave-to {
  opacity: 0;
  transform: scale(0.95) translateY(30px);
}
.list-move {
  transition: transform 0.5s cubic-bezier(0.55, 0, 0.1, 1);
}
</style>

<script>
// Der Script-Teil bleibt identisch zum vorherigen Stand, da sich die Logik nicht ändert.
// Hier zur Vollständigkeit eingefügt.
import api from "@/api"
import signaturePad from "@/components/signaturePad/signaturePad.vue"
import userService from "@/services/userService"

export default {
  components: {
    signaturePad,
  },
  props: {
    showDialog: {
      type: Boolean,
    },
  },
  data() {
    return {
      userService,
      pdfDialog: false,
      pdfStep: 1,
      isFormValid: false,
      rules: {
        required: (value) => !!value || "Dieses Feld ist erforderlich.",
      },
      customerInfos: null,
      loadingTACContract: true,
      error: false,
      contract: null,
      allExistingContracts: null,
      fixTermTimesMissingBefore: [],
      fixTermTimesMissingAfter: [],
      loadingTACContract: false,
      contractEndDate: null,
      allIdlePeriods: [],
      contractStartDate: null,
      contractInformations: null,
      unlockDialog: false,
      selectedContract: null,
      existingRequests: [],
      snackbar: {
        show: false,
        text: "",
        color: "success",
        icon: "mdi-check-circle",
      },
    }
  },
  created() {
    this.init()
    this.fetchContracts()
  },
  methods: {
    triggerReview() {
      if (window.flutter_inappwebview) {
        // Ruft das native Popup auf (falls vom OS erlaubt)
        window.flutter_inappwebview.callHandler("requestAppReview", [false])
      }
    },
    async checkFinancialStatus() {
      if (!this.allExistingContracts) return

      for (let contract of this.allExistingContracts) {
        try {
          const membershipAcc = contract.Accounts.Account.find((acc) => acc.type === "MEMBERSHIP")
          if (!membershipAcc) continue

          const transactionsResponse = await api.fetchAccountTransactionsForIDAdminTESTING(
            membershipAcc.AccountID.UniqueID[0].value,
            this.$moment("2000-01-01", "YYYY-MM-DD").format(),
            this.$moment().add(4, "years").format(),
          )

          if (transactionsResponse && transactionsResponse.AccountTransactions && transactionsResponse.AccountTransactions.AccountTransactions) {
            const transactions = transactionsResponse.AccountTransactions.AccountTransactions

            // Wir filtern NUR die offenen Zahlungen heraus
            const openTransactions = transactions.filter((trans) => trans.Type === "OPEN_PAYMENT" && trans.State !== "PAID")

            // Wir speichern die LISTE der offenen Zahlungen im Vertrag
            this.$set(contract, "openTransactions", openTransactions)
          }
        } catch (e) {
          console.error("Konnte Finanzstatus für Vertrag nicht prüfen", e)
        }
      }
    },
    showNotification(text, type = "success") {
      this.snackbar.text = text
      if (type === "success") {
        this.snackbar.color = "green darken-1"
        this.snackbar.icon = "mdi-check-circle"
      } else if (type === "error") {
        this.snackbar.color = "red darken-1"
        this.snackbar.icon = "mdi-alert-circle"
      } else if (type === "info") {
        this.snackbar.color = "blue darken-1"
        this.snackbar.icon = "mdi-information"
      }
      this.snackbar.show = true
    },
    validateAndProceed() {
      this.$refs.pdfForm.validate()
      if (this.isFormValid) {
        this.pdfStep = 2
      } else {
        this.showNotification("Bitte fülle alle Pflichtfelder aus.", "error")
      }
    },
    async downloadPDF() {
      this.$refs.signature.saveSignature()
      if (!this.customerInfos.signature) {
        this.showNotification("Bitte unterschreiben Sie das Dokument.", "error")
        return
      }

      let contractData = JSON.parse(JSON.stringify(this.selectedContract.request))
      contractData.membershipName = contractData.contract.title
      contractData.startDate = contractData.contract.start
      contractData.endDate = contractData.contract.end

      let data = {
        contract: contractData,
        customerInfos: this.customerInfos,
      }

      try {
        this.showNotification("PDF wird erstellt...", "info")
        await api.createHealthcarePDF(data)
        this.pdfDialog = false
        this.triggerReview()
        setTimeout(() => {
          this.pdfStep = 1
          this.resetSignature()
          this.$refs.pdfForm.resetValidation()
        }, 500)
      } catch (e) {
        this.showNotification("Fehler beim Herunterladen, bitte versuche es erneut.", "error")
      }
    },
    resetSignature() {
      this.customerInfos.signature = null
    },
    saveSignature(e) {
      this.customerInfos.signature = e
    },
    getColorForRequestState(request) {
      if (!request) return "amber" // <-- Neue, freundlichere Farbe
      return request.status === "pending" ? "blue" : "primary"
    },
    getIconForRequestState(request) {
      if (!request) return "mdi-key-variant" // <-- Neues, motivierendes Icon
      return request.status === "pending" ? "mdi-clock-outline" : "mdi-download"
    },
    getTooltipText(request) {
      if (!request) return "Bestätigung anfordern"
      return request.status === "pending" ? "Anfrage in Bearbeitung" : "Bestätigung herunterladen"
    },
    closeForm() {
      this.$emit("closeForm")
    },
    async requestHealthcareFile(contract) {
      this.selectedContract = contract
      console.log("Selected Contract:", contract)
      if (contract.request && contract.request.status === "accepted") {
        this.pdfStep = 1
        this.pdfDialog = true
        return
      }

      if (contract.request && contract.request.status === "pending") {
        this.unlockDialog = true
        return
      }

      if (this.$moment().isBefore(this.$moment(contract.start, "DD.MM.YYYY").subtract(40, "days"))) {
        this.showNotification("Diese Mitgliedschaft ist noch nicht freigabe-bereit.", "info")
        return
      }

      let transactions = []
      try {
        transactions = await api.fetchAccountTransactionsForIDAdminTESTING(
          contract.Accounts.Account.find((acc) => acc.type === "MEMBERSHIP").AccountID.UniqueID[0].value,
          this.$moment("2000-01-01", "YYYY-MM-DD").format(),
          this.$moment().add(4, "years").format(),
        )
      } catch (e) {
        console.log(e)
      }
      console.log(transactions)
      if (
        (!transactions || !transactions.AccountTransactions) &&
        (contract.title.includes("IMPORT") || contract.title.includes("Import") || contract.title.includes("import"))
      ) {
        contract.transactions = []
        this.unlockDialog = true
        return
      }

      transactions =
        transactions.AccountTransactions.AccountTransactions.filter((transaction) => {
          if (!transaction.AccountTransactionDetails.AccountTransactionDetail[0].TimeRange) return false
          let transactionDate = transaction.TransactionDate
          if (contract.originalStart) {
            return (
              this.$moment(transactionDate).isSameOrAfter(this.$moment(contract.originalStart, "YYYY-MM-DD")) &&
              this.$moment(transactionDate).isSameOrBefore(this.$moment(contract.end, "DD.MM.YYYY")) &&
              transaction.type !== "IDLE_PERIOD"
            )
          }
          return (
            this.$moment(transactionDate).isSameOrAfter(this.$moment(contract.start, "DD.MM.YYYY")) &&
            this.$moment(transactionDate).isSameOrBefore(this.$moment(contract.end, "DD.MM.YYYY")) &&
            transaction.type !== "IDLE_PERIOD"
          )
        }) || []

      if (transactions.find((trans) => trans.State === "OPEN" || (trans.Type === "OPEN_PAYMENT" && trans.State !== "PAID"))) {
        this.showNotification("Bitte begleiche zuerst alle offenen Zahlungen für diese Mitgliedschaft.", "error")
        return
      }

      contract.transactions = transactions.map((trans) => {
        return {
          start: trans.AccountTransactionDetails.AccountTransactionDetail[0].TimeRange.Start,
          end: trans.AccountTransactionDetails.AccountTransactionDetail[0].TimeRange.End,
          cost: trans.Value,
          state: trans.State,
          type: trans.Type,
          mainType: trans.MainType,
          det: trans.AccountTransactionDetails,
        }
      })

      this.unlockDialog = true
    },
    handleUnlockAction() {
      if (!this.selectedContract.request) {
        this.sendUnlockRequest()
      } else if (this.selectedContract.request.status === "accepted") {
        this.unlockDialog = false
        this.pdfDialog = true
      }
    },
    async fetchContracts() {
      let error = false
      let contract = null
      try {
        this.existingRequests = await api.fetchHealthcareUnlockRequests()
        let allConts = await api.fetchTACAllMemberships()
        this.allExistingContracts = allConts.MemberDetails.MemberDetail

        this.checkFinancialStatus()
      } catch (e) {
        error = true
        console.log(e)
        this.showNotification("Mitgliedschaft konnte nicht gefunden werden.", "error")
        this.$router.push("/user")
      }
      this.loadingTACContract = false
    },
    async init() {
      let customer = await api.getOwnUserForManager()
      let customerEntry = {
        selection: customer._id,
        gender: customer.geschlecht,
        firstName: customer.vorname,
        lastName: customer.nachname,
        email: customer.email,
        phone: customer.telefon,
        birthday: customer.geburtstag,
        street: customer.adresse_1,
        city: customer.stadt,
        zip: customer.plz,
        signature: null,
      }
      this.customerInfos = customerEntry
    },
    bonusCalculatedFromMultipleTimespans(allTimeSpans, timeSpanDays) {
      const totalDays = allTimeSpans.reduce((total, timeSpan) => {
        return total + this.daysInTimeSpan(timeSpan)
      }, 0)

      const diff = Math.abs(Number(totalDays) - Number(timeSpanDays))
      console.log(diff)
      return diff <= 3
    },
    daysInTimeSpan(timeSpan) {
      const start = this.$moment(timeSpan.TimeSpan.Start, "YYYY-MM-DD")
      const end = this.$moment(timeSpan.TimeSpan.End, "YYYY-MM-DD")
      return end.diff(start, "days")
    },
    async sendUnlockRequest() {
      try {
        this.selectedContract.title = this.selectedContract.title.replace("Flexi", "Monatsabonnement Kombi")
        if (this.selectedContract.title.includes("Group Fitness") || this.selectedContract.title.includes("GroupFitness")) {
          this.selectedContract.title = "Group Fitness"
        }
        await api.requestHealthcareUnlock({
          contract: this.selectedContract,
        })
        this.unlockDialog = false
        this.fetchContracts()
        this.showNotification("Anfrage erfolgreich gesendet!", "success")
      } catch (e) {
        this.showNotification("Ein Fehler ist aufgetreten, bitte versuche es erneut.", "error")
      }
    },
  },
  computed: {
    unlockDialogTitle() {
      if (!this.selectedContract) return ""
      if (!this.selectedContract.request) return "Freischaltung anfordern"
      return this.selectedContract.request.status === "pending" ? "Anfrage in Bearbeitung" : "Bestätigung bereit"
    },
    unlockDialogBtnText() {
      if (!this.selectedContract) return ""
      if (!this.selectedContract.request) return "Jetzt anfordern"
      return this.selectedContract.request.status === "pending" ? "" : "Herunterladen"
    },
    unlockDialogText() {
      if (!this.selectedContract) return ""
      if (!this.selectedContract.request)
        return "Wir prüfen deine Anfrage schnellstmöglich und schalten die Bestätigung frei, sobald alle Kriterien erfüllt sind."
      return this.selectedContract.request.status === "pending"
        ? "Deine Anfrage wird aktuell geprüft. Wir benachrichtigen dich, sobald sie freigegeben wurde. Bitte habe etwas Geduld."
        : "Deine Bestätigung wurde freigeschaltet und kann jetzt heruntergeladen werden."
    },
    contractTimespans() {
      if (!this.allExistingContracts?.length) return []

      const splitTimespanIntoYearChunks = (timespan) => {
        const startMoment = this.$moment(timespan.TimeSpan.Start, "YYYY-MM-DD")
        const endMoment = this.$moment(timespan.TimeSpan.End, "YYYY-MM-DD")
        const result = []
        let currentStart = startMoment.clone()
        while (currentStart.isBefore(endMoment)) {
          let currentEnd = currentStart.clone().add(1, "year").subtract(1, "day")
          if (currentEnd.isAfter(endMoment)) {
            currentEnd = endMoment.clone()
          }
          result.push({
            ContractID: timespan.ContractID,
            Type: timespan.Type,
            originalTimeSpan: timespan.TimeSpan,
            TimeSpan: {
              Start: currentStart.format("YYYY-MM-DD"),
              End: currentEnd.format("YYYY-MM-DD"),
            },
          })
          currentStart = currentEnd.clone().add(1, "day")
        }
        result.forEach((ts) => {
          ts.yearCount = result.length
        })
        return result
      }

      let allContracts = []

      for (let contract of this.allExistingContracts) {
        let timeSpans = contract.ContractTermTimes.ContractTermTime
        timeSpans = this.fixTermTimesMissingBefore.concat(timeSpans, this.fixTermTimesMissingAfter)

        let splittedTimeSpans = []
        timeSpans.forEach((ts) => {
          const start = this.$moment(ts.TimeSpan.Start, "YYYY-MM-DD")
          const end = this.$moment(ts.TimeSpan.End, "YYYY-MM-DD")
          if (end.diff(start, "years", true) > 1) {
            splittedTimeSpans = splittedTimeSpans.concat(splitTimespanIntoYearChunks(ts))
          } else {
            splittedTimeSpans.push(ts)
          }
        })
        timeSpans = splittedTimeSpans

        let activeContractDate = null
        if (
          timeSpans.length &&
          timeSpans.find((ts) => ts.Type === "DEFAULT" && !this.$moment(ts.TimeSpan.Start, "YYYY-MM-DD").isAfter(this.$moment(), "day"))
        ) {
          activeContractDate = JSON.parse(JSON.stringify(timeSpans))
            .reverse()
            .find((ts) => ts.Type === "DEFAULT" && !this.$moment(ts.TimeSpan.Start, "YYYY-MM-DD").isAfter(this.$moment(), "day")).TimeSpan.Start
        }

        let contracts = []
        let newContract = null
        // Hier holen wir alle Ruhezeiten des gesamten Vertrags
        let idlePeriodTimeSpans = timeSpans.filter((timespan) => timespan.Type === "IDLEPERIOD")

        timeSpans.forEach((timespan) => {
          // --- ÄNDERUNG: Filter ENTFERNT ---
          // Wir filtern nicht mehr nach Datum. Wenn ein Zeitblock exakt so lang ist
          // wie die Summe der Ruhezeiten, dann gehört er dazu (Timestop Bonus).

          if (timespan.Type === "DEFAULT") {
            let isTimeMatch = false
            if (newContract && idlePeriodTimeSpans.length) {
              // Check 1: Passt der Block zur SUMME aller Ruhezeiten? (Dein "zusammengefasst"-Szenario)
              const matchSum = this.bonusCalculatedFromMultipleTimespans(idlePeriodTimeSpans, this.daysInTimeSpan(timespan))

              // Check 2: Passt der Block zu EINER EINZELNEN Ruhezeit? (Dein "2 Timestops, 2 Verlängerungen"-Szenario)
              // Wir prüfen, ob IRGENDEINE Ruhezeit von der Dauer her (+/- 3 Tage) zu diesem Block passt.
              const matchSingle = idlePeriodTimeSpans.some((idlePeriod) => {
                const diff = Math.abs(this.daysInTimeSpan(idlePeriod) - this.daysInTimeSpan(timespan))
                return diff <= 3
              })

              // Wenn eines von beiden zutrifft, ist es eine gültige Verlängerung
              isTimeMatch = matchSum || matchSingle
            }

            if (isTimeMatch) {
              // Es ist eine Verlängerung (Dauer matcht die Ruhezeit) -> Merge
              newContract.timeStopBonus.push({
                start: this.$moment(timespan.TimeSpan.Start, "YYYY-MM-DD").format("DD.MM.YYYY"),
                end: this.$moment(timespan.TimeSpan.End, "YYYY-MM-DD").format("DD.MM.YYYY"),
              })
            } else {
              // Es ist ein reguläres Jahr -> Neuer Eintrag
              if (newContract !== null) contracts.push(newContract)

              let isTermUnpaid = false

              // Haben wir offene Zahlungen im Hauptvertrag gefunden?
              if (contract.openTransactions && contract.openTransactions.length) {
                // Start und Ende dieser aktuellen Laufzeit
                const termStart = this.$moment(timespan.TimeSpan.Start, "YYYY-MM-DD")
                const termEnd = this.$moment(timespan.TimeSpan.End, "YYYY-MM-DD")

                // Prüfen, ob eine offene Zahlung diesen Zeitraum betrifft
                isTermUnpaid = contract.openTransactions.some((trans) => {
                  console.log(trans)
                  const details = trans.AccountTransactionDetails?.AccountTransactionDetail?.[0]
                  if (!details || !details.TimeRange) return false

                  const transStart = this.$moment(details.TimeRange.Start)
                  const transEnd = this.$moment(details.TimeRange.End)

                  // LOGIK: Üb.erschneidung der Zeiträume prüfen
                  // Eine Rechnung gehört zur Laufzeit, wenn sich die Zeiträume überschneiden.
                  // Oft ist Startdatum Rechnung == Startdatum Laufzeit.

                  return termStart.isSameOrBefore(transEnd) && termEnd.isSameOrAfter(transStart)
                })
              }

              newContract = {
                yearCount: timespan.yearCount,
                originalStart: timespan.originalTimeSpan ? timespan.originalTimeSpan.Start : null,
                ContractID: timespan.ContractID.UniqueID[0].value,
                title: contract.MemberCategoryName,
                Accounts: contract.Accounts,
                hasOpenPayment: isTermUnpaid,
                start: this.$moment(timespan.TimeSpan.Start, "YYYY-MM-DD").format("DD.MM.YYYY"),
                end: this.$moment(timespan.TimeSpan.End, "YYYY-MM-DD").format("DD.MM.YYYY"),
                bonus: [],
                propertyID: contract.PropertyID.UniqueID[0].value,
                timeStopBonus: [],
                idlePeriods: [],
                active: activeContractDate === timespan.TimeSpan.Start,
                request: this.existingRequests.find(
                  (req) =>
                    req.contract.ContractID === timespan.ContractID.UniqueID[0].value &&
                    req.contract.start === this.$moment(timespan.TimeSpan.Start, "YYYY-MM-DD").format("DD.MM.YYYY") &&
                    req.contract.end === this.$moment(timespan.TimeSpan.End, "YYYY-MM-DD").format("DD.MM.YYYY"),
                ),
                future: this.$moment(timespan.TimeSpan.Start, "YYYY-MM-DD").isAfter(this.$moment()),
              }
            }
          }

          if (timespan.Type === "BONUSPERIOD") {
            newContract.bonus.push({
              start: this.$moment(timespan.TimeSpan.Start, "YYYY-MM-DD").format("DD.MM.YYYY"),
              end: this.$moment(timespan.TimeSpan.End, "YYYY-MM-DD").format("DD.MM.YYYY"),
            })
          }
        })

        contracts.push(newContract)

        // Bereinigung von Verträgen, die komplett in Ruhezeiten liegen
        idlePeriodTimeSpans.forEach((timespan) => {
          let foundContract = contracts.find((cont) => {
            if (!cont) return false
            let contEndDate = cont.timeStopBonus.length
              ? cont.timeStopBonus[cont.timeStopBonus.length - 1].end
              : cont.bonus.length
              ? cont.bonus[cont.bonus.length - 1].end
              : cont.end
            return this.$moment(timespan.TimeSpan.Start, "YYYY-MM-DD").isBetween(
              this.$moment(cont.start, "DD.MM.YYYY"),
              this.$moment(contEndDate, "DD.MM.YYYY"),
            )
          })
          if (!foundContract) return
        })

        if (!contracts.length && newContract && newContract.title) {
          contracts.push(newContract)
        }

        if (!contracts.find((cont) => cont && cont.active && !cont.future)) {
          if (contracts.length > 0 && contracts[contracts.length - 1]) {
            contracts[contracts.length - 1].active = true
          }
        }

        // --- 3-Jahres Filter ---
        let years = this.userService.hasRight("d_v_healthcare") ? 30 : 3
        const threeYearsAgo = this.$moment().subtract(years, "years")

        let recentContracts = contracts.filter((c) => {
          if (!c || !c.end) return false
          return this.$moment(c.end, "DD.MM.YYYY").isAfter(threeYearsAgo)
        })

        if (recentContracts.length === 0) {
          continue
        }

        if (allContracts.length) {
          let pushed = false
          for (let existingContract of allContracts) {
            if (existingContract.length && existingContract[0].title === recentContracts[0].title) {
              for (let conti of recentContracts) {
                existingContract.push(conti)
              }
              pushed = true
            }
          }
          if (!pushed) {
            allContracts.push(recentContracts)
          }
        } else {
          allContracts.push(recentContracts)
        }
      }

      return allContracts.map((contract) => {
        return contract.reverse()
      })
    },
  },
}
</script>
